import styled, { css } from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Transition from '@/components/Common/Transition';
import FlexibleTextarea from '@/components/Inputs/FlexibleTextarea';
import Input from '@/components/Inputs/Input';
import { StyledTooltip } from '@/components/Tooltip/TooltipRenderer';
import Typo from '@/components/Typography/Typo';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import InlineKeywordFilterV2 from '@/features/StructuredFilter/components/InlineKeywordFilterV2';
import { radius } from '@/styles/sizes';

import type { CandidateItemT } from './types';

type Props = {
  index: number;
  selected: boolean;
  candidateItem: CandidateItemT;
  fetchPreview: (index: number, rule?: AIRulePromptSet) => void;
  onTitleChange: (index: number, name: string) => string;
  onRuleChange: (index: number, rule: AIRule) => void;
  onPromptChange: (
    index: number,
    prompt: AIRulePromptSet,
    changed: boolean,
  ) => void;
  editDisabled?: boolean;
  selectPreview?: (index: number) => void;
  onDeleteClick?: (index: number) => void;
};

const AITopicCandidateItem = (props: Props) => {
  const filter = new SFilter(props.candidateItem.ai_rule.filter);

  const onKeywordFilterChange = (
    newKeywordFilter: StructuredFilterStringNumberValueSet | null,
    restriction: 'include' | 'exclude',
  ) => {
    if (restriction === 'include') {
      filter.setIncludeSnippetFilter(newKeywordFilter);
    } else {
      filter.setExcludeSnippetFilter(newKeywordFilter);
    }

    props.onRuleChange(props.index, {
      ...props.candidateItem.ai_rule,
      filter: filter.get(),
    });
  };

  return (
    <SPromptItem g={32} f={1} j="space-between" selected={props.selected}>
      <VFlex g={24}>
        <VFlex g={12}>
          <Typo type="label2" weight="sb">
            Title
          </Typo>
          <Flex j="space-between" a="flex-end">
            {props.editDisabled ? (
              <Typo type="label0" weight="sb">
                {props.candidateItem.name}
              </Typo>
            ) : (
              <Input
                css={css`
                  background-color: ${(props) => props.theme.colors.gray100};
                  border-radius: ${radius.token.lg}px;
                  padding: 8px;
                `}
                sizeType="l"
                defaultValue={props.candidateItem.name}
                disabled={props.candidateItem.isLoading}
                style={{
                  fontWeight: 600,
                }}
                onBlur={(e) => {
                  const newValue = props.onTitleChange(
                    props.index,
                    e.target.value,
                  );
                  e.target.value = newValue;
                }}
              />
            )}
          </Flex>
        </VFlex>
        <VFlex g={12}>
          <Typo type="label2" weight="sb">
            Prompt
          </Typo>
          {props.editDisabled ? (
            <Typo type="body2">{props.candidateItem.ai_rule.prompt}</Typo>
          ) : (
            <FlexibleTextarea
              css={css`
                background-color: ${(props) => props.theme.colors.gray100};
                border-radius: ${radius.token.lg}px;
                padding: 8px;
              `}
              value={props.candidateItem.ai_rule?.prompt}
              disabled={props.candidateItem.isLoading}
              onChange={(e) => {
                props.onPromptChange(
                  props.index,
                  {
                    prompt_en: props.candidateItem.ai_rule?.prompt_en || '',
                    prompt: e.currentTarget.value,
                  },
                  true,
                );
              }}
              showBorder
              minHeight={84}
            />
          )}
        </VFlex>
        <VFlex g={8}>
          <SFilterRow>
            <SLabelText type="label2" weight="sb">
              Filter
            </SLabelText>
          </SFilterRow>
          <SFilterRow>
            <SLabelText>Include</SLabelText>
            <InlineKeywordFilterV2
              name={`topic-include-keyword-filter-${props.index}`}
              keywordFilter={filter.getIncludeSnippetFilter()}
              onChange={(f) => onKeywordFilterChange(f, 'include')}
              allowSpace
              restriction="include"
            />
          </SFilterRow>
          <SFilterRow>
            <SLabelText>Exclude</SLabelText>
            <InlineKeywordFilterV2
              name={`topic-exclude-keyword-filter-${props.index}`}
              keywordFilter={filter.getExcludeSnippetFilter()}
              onChange={(f) => onKeywordFilterChange(f, 'exclude')}
              allowSpace
              restriction="exclude"
            />
          </SFilterRow>
        </VFlex>
      </VFlex>
      <Flex j="space-between" a="center">
        <Flex>
          {props.onDeleteClick && (
            <Button
              variant="secondary"
              sizeType="m"
              onClick={() => props.onDeleteClick?.(props.index)}
            >
              Delete this topic
            </Button>
          )}
        </Flex>

        <Flex style={{ position: 'relative' }}>
          {props.selectPreview && (
            <Button
              variant="ghost"
              sizeType="m"
              onClick={() => props.selectPreview?.(props.index)}
              isLoading={props.candidateItem.isLoading}
            >
              See preview
            </Button>
          )}

          <Button
            variant="primary"
            sizeType="m"
            onClick={() => props.fetchPreview(props.index)}
            isLoading={props.candidateItem.isLoading}
            disabled={props.candidateItem.readyState}
          >
            Update preview
          </Button>
          <Transition
            status={
              !props.candidateItem.isLoading && !props.candidateItem.readyState
            }
          >
            <StyledTooltip
              style={{ position: 'absolute', right: 0, bottom: 40 }}
            >
              Update and check the preview
            </StyledTooltip>
          </Transition>
        </Flex>
      </Flex>
    </SPromptItem>
  );
};

type StyleProps = {
  selected: boolean;
};

const SPromptItem = styled(VFlex)<StyleProps>`
  position: relative;
  padding: 24px;
  background-color: ${(props) => props.theme.colors.gray200};
  border-radius: ${radius.card}px;
  border: 1px solid transparent;
  transition: border-color 0.2s;
  .cover {
    top: 0;
    left: 0;
    cursor: not-allowed;
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
    background-color: ${(props) => props.theme.colors.NL40};
  }
  ${({ selected }) =>
    selected &&
    css`
      border-color: ${(props) => props.theme.colors.primary100};
    `}
`;

const SLabelText = styled(Typo)`
  color: ${(props) => props.theme.colors.gray800};
  width: 80px;
  flex-shrink: 0;
`;

const SFilterRow = styled(Flex)`
  align-items: center;
`;

export default AITopicCandidateItem;
